import ReactionsIcon from '@/CustomIcons/ReactionsIcon'
import { Button } from '@/UIComponents2/Button'
import MenuButton from '@/UIComponents2/MenuButton/MenuButton'
import { v4 as uuidv4 } from 'uuid'
import { wsConnection } from '@/services/WSConnectionInstance'
import { useCallback, useContext, useMemo, useState } from 'react'
import { RoomContext, RoomContextType } from '@/RoomContext'
import { UserContext, UserContextType } from '@/UserContext'
import { Tabs } from '@ark-ui/react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utlis/cn'
import Tooltip from '@/UIComponents2/Tooltip'
import EmojiAddIcon from '@/CustomIcons/EmojiAddIcon'
import MusicNoteIcon from '@/CustomIcons/MusicNoteIcon'
import { throttle } from 'lodash'
import { TextInput } from '@/UIComponents2/TextInput'
import SearchIcon from '@/CustomIcons/SearchIcon'
import ReactionOverlay from '../shared/ReactionOverlay'

const EMOJI_LIST = [
    { emoji: '👍', tags: 'thumbsup like approve yes agree' },
    { emoji: '😂', tags: 'laugh haha rofl lmao funny' },
    { emoji: '❤️', tags: 'love heart like favorite' },
    { emoji: '🔥', tags: 'fire lit hot trending cool' },
    { emoji: '👏', tags: 'clap applause bravo welldone' },
    { emoji: '😢', tags: 'cry sad tear upset' },
    { emoji: '😡', tags: 'angry mad rage annoyed' },
    { emoji: '🤔', tags: 'thinking hmm curious question' },
    { emoji: '🙌', tags: 'praise celebrate yay success' },
    { emoji: '🥳', tags: 'party celebrate birthday congrats' },
    { emoji: '🤯', tags: 'mindblown shock wow surprise' },
    { emoji: '😎', tags: 'cool sunglasses chill confident' },
    { emoji: '🤝', tags: 'handshake deal agree partnership' },
    { emoji: '💯', tags: '100 perfect agree truth' },
    { emoji: '✅', tags: 'check yes done complete correct' },
    { emoji: '❌', tags: 'x no wrong cancel stop' },
    { emoji: '🙏', tags: 'pray please thanks gratitude' },
    { emoji: '🤮', tags: 'sick gross disgust puke' },
    { emoji: '🥺', tags: 'pleading cute please puppyeyes' },
    { emoji: '😴', tags: 'sleep tired boring snooze' },
    { emoji: '🤡', tags: 'clown joke fool silly' },
    { emoji: '👀', tags: 'eyes look watching curious' },
    { emoji: '🎉', tags: 'party celebrate confetti congrats' },
    { emoji: '😅', tags: 'sweat relief nervous awkward' },
    { emoji: '🤗', tags: 'hug comfort support love' },
    { emoji: '😇', tags: 'angel innocent pure good' },
    { emoji: '💔', tags: 'brokenheart sad breakup hurt' },
    { emoji: '😏', tags: 'smirk sly flirty smug' },
    { emoji: '🤬', tags: 'cursing angry swear rage' },
    { emoji: '🥶', tags: 'cold freezing ice chill' },
    { emoji: '🥵', tags: 'hot heat sweating spicy' },
    { emoji: '😜', tags: 'wink tongue silly playful' },
    { emoji: '😱', tags: 'scream shock scared horror' },
    { emoji: '🤩', tags: 'starstruck amazed wow excited' },
    { emoji: '😋', tags: 'yum tasty delicious food' },
    { emoji: '😐', tags: 'neutral meh blank indifferent' },
    { emoji: '🙄', tags: 'eyeroll annoyed whatever' },
    { emoji: '😬', tags: 'grimace awkward cringe' },
    { emoji: '😈', tags: 'devil evil mischievous naughty' },
]

function searchEmojis(query: string): string[] {
    const q = query.toLowerCase().trim()
    if (q === '') {
        return EMOJI_LIST.map((emoji) => emoji.emoji)
    }
    return EMOJI_LIST.filter((item) => item.tags.includes(q)).map((emoji) => emoji.emoji)
}

const MIN_REACTION_DELAY = 500

type ReactionsMenuButtonProps = {
    labelPosition?: 'inside' | 'outside'
    triggerClassName?: string
}
const ReactionsMenuButton = (props: ReactionsMenuButtonProps) => {
    const { state: roomState } = useContext(RoomContext) as RoomContextType
    const { state: userState } = useContext(UserContext) as UserContextType
    const [q, setQ] = useState('')
    const { t } = useTranslation()

    const [value, setValue] = useState<'emoji' | 'sound'>('emoji')

    const sendReaction = useCallback(
        (reaction: string) => {
            const tempUuid = uuidv4()

            wsConnection.sendMessage({
                type: 'send-room-reaction',
                data: {
                    uuid: tempUuid,
                    senderUrn: userState.userUrn,
                    roomUrn: roomState.roomUrn,
                    timestamp: new Date(),
                    body: reaction,
                    senderSessionId: userState.sessionId,
                    clientType: userState.clientType,
                    authToken: userState.authToken,
                },
            })
        },
        [userState.userUrn, roomState.roomUrn, userState.sessionId]
    )

    const handleReactionSelect = throttle((emoji: string) => {
        sendReaction(emoji)
    }, MIN_REACTION_DELAY)

    const tabStyles = {
        base: [
            'inline-flex',
            'gap-[10px]',
            'items-center',
            'justify-center',
            'h-[35px]',
            'disabled:cursor-not-allowed',
            'disabled:opacity-50',
            'disabled:w-full',
            'disabled:px-[10px]',
            'w-full',
            'font-light',
            'focus:outline-none',
            'opacity-75',
            'border-b-[1px]',
            'border-b-[rgba(255,255,255,0.1)]',
            'pb-[15px]',
            'whitespace-nowrap',
        ],
        active: ['font-medium', 'opacity-100', 'border-b-[2px]', 'border-b-[rgba(255,255,255,1)]'],
    }

    const emojis = useMemo(() => searchEmojis(q ?? ''), [q])

    return (
        <>
            <MenuButton
                label={t('Reactions')}
                icon={<ReactionsIcon style={{ fontSize: '1.3rem' }} />}
                labelPosition={props.labelPosition}
                triggerClassName={props.triggerClassName}
                content={
                    <Tabs.Root
                        value={value}
                        defaultValue="emoji"
                        onValueChange={(e) => setValue(e.value as 'emoji' | 'sound')}
                    >
                        <Tabs.List className="pb-4 flex">
                            <Tabs.Trigger
                                value="emoji"
                                className={cn(tabStyles.base, value === 'emoji' ? tabStyles.active : '')}
                            >
                                <EmojiAddIcon style={{ fontSize: '1.3rem' }} /> {t('Emoji Reaction')}
                            </Tabs.Trigger>
                            <Tooltip text={'This premium feature has not yet been released.'}>
                                <Tabs.Trigger
                                    value="sound"
                                    disabled
                                    className={cn(tabStyles.base, value === 'sound' ? tabStyles.active : '')}
                                >
                                    <MusicNoteIcon style={{ fontSize: '1.3rem' }} /> {t('Sound Reaction')}
                                </Tabs.Trigger>
                            </Tooltip>
                        </Tabs.List>
                        <Tabs.Content value="emoji">
                            <div className="pb-4">
                                <TextInput
                                    onChange={(query) => setQ(query)}
                                    className="py-[8px]"
                                    placeholder="Search"
                                    icon={<SearchIcon />}
                                />
                            </div>
                            <div className="grid grid-cols-6 gap-[5px] max-h-[150px] overflow-y-auto">
                                {emojis.map((emoji) => (
                                    <Button variant="tertiary" key={emoji} onClick={() => handleReactionSelect(emoji)}>
                                        {emoji}
                                    </Button>
                                ))}
                            </div>
                        </Tabs.Content>
                        <Tabs.Content value="sound">
                            <div className="grid grid-cols-6 gap-[5px]"></div>
                        </Tabs.Content>
                    </Tabs.Root>
                }
            />
            <ReactionOverlay userUrn="viewer" />
        </>
    )
}

export default ReactionsMenuButton
